import { useConfigStore } from '@dashboard/stores/configStore'
import { usePermissionStore } from '@dashboard/stores/permissionStore'
import { useSubscriptionStore } from '@dashboard/stores/subscriptionStore'
import type { RenewalPlan } from '@dashboard/types/user'
import { calendarDaysUntil } from '@dashboard/utils/trialDates'
import { useIntervalFn, useNow } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

/**
 * Trial states shown by the sidebar trial status and the billing dialog.
 *
 * - early / last / today / expired: a no-card trial without paid renewal.
 * - activated: a no-card trial whose owner confirmed a plan.
 * - cardtrial: a card trial that already renews.
 * - collab: the viewer can't manage billing on a running, non-renewing trial.
 */
export type TrialStatusState = 'early' | 'last' | 'today' | 'expired' | 'activated' | 'cardtrial' | 'collab'

const LAST_DAYS = 3

export function useTrialStatus() {
  const configStore = useConfigStore()
  const { owner } = storeToRefs(configStore)
  const subscriptionStore = useSubscriptionStore()
  const {
    trialActivation,
    isTrialing,
    isTrialExpired,
    isFreeAccount,
    isAdmin,
    isFreeBuilder,
    isLegacyBilling,
    hasPaymentMethod
  } = storeToRefs(subscriptionStore)
  const permissionStore = usePermissionStore()
  const now = useNow({ scheduler: (cb) => useIntervalFn(cb, 60_000) })

  const canManageBilling = computed(() => permissionStore.canAccess('manage-billing') === true)
  const renewalPlan = computed<RenewalPlan | null>(() => owner.value?.renewal_plan ?? null)

  const exempt = computed(
    () =>
      !owner.value ||
      isFreeAccount.value === true ||
      isAdmin.value === true ||
      isFreeBuilder.value ||
      isLegacyBilling.value ||
      owner.value.requires_reactivation === true ||
      Boolean(owner.value.requires_payment_recovery)
  )

  const endsAt = computed<Date | null>(() => {
    if (trialActivation.value) {
      return new Date(trialActivation.value.trial_end * 1000)
    }
    if (renewalPlan.value) {
      return new Date(renewalPlan.value.starts_at)
    }
    return null
  })

  const expired = computed(() => !!trialActivation.value && isTrialExpired.value)

  const daysLeft = computed<number | null>(() => {
    if (!endsAt.value || expired.value) {
      return null
    }
    return Math.max(0, calendarDaysUntil(endsAt.value, now.value))
  })

  const renewalEnabled = computed(() => !trialActivation.value && renewalPlan.value !== null)

  const state = computed<TrialStatusState | null>(() => {
    if (exempt.value) {
      return null
    }
    if (trialActivation.value) {
      if (expired.value) {
        return 'expired'
      }
      if (!canManageBilling.value) {
        return 'collab'
      }
      const days = daysLeft.value ?? 0
      if (days === 0) {
        return 'today'
      }
      return days <= LAST_DAYS ? 'last' : 'early'
    }
    if (isTrialing.value && renewalPlan.value) {
      return renewalPlan.value.trial_kind === 'no_card' ? 'activated' : 'cardtrial'
    }
    return null
  })

  const ownerFirstName = computed(() => owner.value?.name?.trim().split(/\s+/)[0] ?? '')

  return {
    state,
    daysLeft,
    endsAt,
    expired,
    renewalEnabled,
    renewalPlan,
    hasSavedCard: hasPaymentMethod,
    canManageBilling,
    ownerFirstName
  }
}
